import {
  IBundleSaving,
  ProtocolDosingRow,
  ProtocolInfoRes,
  ProtocolWithDosingsRes,
  enforceDosingsTypes,
  enforceProtocolInfoTypes,
  enforceProtocolWithDosingsTypes } from "@protoplan/types";
import { axiosInstance } from "./axiosInstance";
import { enforceDeliveryPerListingType } from "../types/listing";




export async function updateNumericValues(idsAndValues: { dosingId: number, value: number }[], label: string)
{
  return axiosInstance.post("/protocol/updateNumericValues", {
    "Ids-and-values": idsAndValues,
    "Label": label })
    .then(resJson => resJson.data as { success: boolean })
    .catch(error => console.log("Error updating protocol values:", error));
}

export async function deleteDosings(dosingIds: number[])
{
  return axiosInstance.post("/protocol/removeDosings", { "Remove-from-protocol": dosingIds })
    .then(resJson => resJson.data)
    .catch(error => console.log("Error deleting from protocol:", error));
}

export async function addDosing(
  protocolId: number,
  listingId?: number,
  productId?: number,
  appliedDiscountIds?: number[])
{
  return axiosInstance.post("/protocol/addDosing", { listingId, productId, protocolId, appliedDiscountIds })
    .catch((error: Error) => console.log("Error adding to protocol:", error));
}

export async function swapListing(listingId: number, dosingId: number)
{
  return axiosInstance.post("/protocol/swapListing", { listingId, dosingId })
    .then(resJson => resJson.data.dosings as ProtocolDosingRow[])
    .catch(error => console.log("Error swapping into protocol:", error));
}

export async function swapBundle(bundleSaving: IBundleSaving, protocolId: number)
{
  return axiosInstance.post("/protocol/swapBundle", { bundleSaving, protocolId })
    .then(resJson => resJson.data as ProtocolDosingRow[])
    .catch(error => console.log("Error swapping bundle into protocol:", error));
}

export async function swapProduct(dosingId: number, protocolId: number, productId: number)
{
  return axiosInstance.post("/protocol/swapProduct", { dosingId, protocolId, productId })
    .then(resJson => resJson.data)
    .catch(error => console.log("Error finding product from same vendor:", error));
}


export async function getProtocol(protocolId: number)
{
  return axiosInstance.post("/protocol/protocol", { protocolId })
    .then(resJson =>
    {
      const protocol = resJson.data as ProtocolWithDosingsRes | null | undefined;
      if(!protocol)
        return undefined;

      const typedProtocol = enforceProtocolWithDosingsTypes(protocol);

      return { ...typedProtocol, dosings: enforceDeliveryPerListingType(typedProtocol.dosings) };
    })
    .catch(error => console.log("Error retrieving protocol:", error));
}

export async function getDosings(protocolId: number, savings = false)
{
  return axiosInstance.post("/protocol/dosings", { protocolId, savings })
    .then((resJson: { data: ProtocolDosingRow[] } | void) =>
    {
      if(!resJson?.data)
        throw("");

      return enforceDeliveryPerListingType(enforceDosingsTypes(resJson.data));
    })
    .catch(error => console.log("Error retrieving dosings:", error));
}

export async function getProtocols()
{
  return axiosInstance.post("/protocol/protocols")
    .then(resJson => (resJson.data.protocols as ProtocolInfoRes[]).map(p => enforceProtocolInfoTypes(p)))
    .catch(error => console.log("Error retrieving protocol:", error));
}

export async function postNewProtocol(name: string, folderId?: number, budget?: number)
{
  return axiosInstance.post("/protocol/new", { name, folderId, budget })
    .then(resJson => (resJson.data as { protocolId: number}).protocolId)
    .catch(error => console.log("Error creating new protocol:", error));
}

export async function deleteAllFromProtocol()
{
  return axiosInstance.post("/protocol/clear", {
    "Clear-protocol": true })
    .then(resJson => resJson.data)
    .catch(error => console.log("Error clearing protocol:", error));
}

export async function updateBudget(budget: number, protocolId: number)
{
  return axiosInstance.post("/protocol/updateBudget", { protocolId, budget })
    .catch(error => console.log("Error updating budget:", error));
}

export async function deleteProtocol(protocolId: number)
{
  return axiosInstance.post("/protocol/delete", { protocolId })
    .catch(error => console.log("Error deleting protocolId " + protocolId + ":", error));
}

export async function removeSharedProtocol(protocolId: number, userId?: number)
{
  return axiosInstance.post("/protocol/removeShared", { protocolId, userId })
    .catch(error => console.log("Error removing shared protocolId " + protocolId + ":", error));
}

export async function updateName(name: string, protocolId: number)
{
  return axiosInstance.post("/protocol/updateName", { protocolId, name })
    .catch(error => console.log("Error updating name for protocolId " + protocolId + ":", error));
}

export async function updateDiscount(dosingId: number, discountId: number)
{
  return axiosInstance.post("/protocol/applyDiscount", { dosingId, discountId })
    .catch(error => console.log("Error toggling discount " + discountId + " for dosingId " + dosingId + ":", error));
}

export async function postOrderReminderActive(protocolId: number, active: boolean)
{
  return axiosInstance.post("/protocol/updateOrderReminderActive", { protocolId, active })
    .catch(error => console.log("Error updating protocol order reminder active " +
      "to " + active + " " +
      "for protocolId " + protocolId + ":", error));
}

export async function postFees(protocolId: number, fees: boolean)
{
  return axiosInstance.post("/protocol/fees", { protocolId, fees })
    .catch(error => console.log("Error updating protocol fees " +
      "to " + fees + " " +
      "for protocolId " + protocolId + ":", error));
}

export async function postCurrency(protocolId: number, currencyId: number)
{
  return axiosInstance.post("/protocol/currency", { protocolId, currencyId })
    .catch(error => console.log("Error updating protocol currencyId " +
      "to " + currencyId + " " +
      "for protocolId " + protocolId + ":", error));
}

export async function share(protocolId: number, userId?: number, email?: string)
{
  return axiosInstance.post("/protocol/share", { protocolId, userId, email })
    .then(resJson => resJson.data as { username?: string, success: boolean })
    .catch(error => console.log(
      "Error sharing protocolId " + protocolId + " " +
      "with email " + email + ":", error));
}

export async function updateEdit(protocolId: number, userId: number, edit: boolean)
{
  return axiosInstance.post("/protocol/updateEdit", { protocolId, userId, edit })
    .then(resJson => resJson.data as { username: string })
    .catch(error => console.log(
      "Error updating edit permissions on protocolId " + protocolId + " " +
      "for user " + userId + " " +
      "to " + edit + ":", error));
}

export async function updatePublic(protocolId: number, public_: boolean)
{
  return axiosInstance.post("/protocol/updatePublic", { protocolId, public: public_ })
    .catch(error => console.log("Error updating public viewing permission on " + protocolId + ":", error));
}

export async function updateNote(dosingId: number, note?: string)
{
  return axiosInstance.post("/protocol/updateNote", { dosingId, note })
    .catch(error => console.log("Error updating note of dosingId " + dosingId + ":", error));
}

export async function postDuplicate(protocolId: number, folderId?: number, name?: string)
{
  return axiosInstance.post("/protocol/duplicate", { protocolId, folderId, name })
    .then(res =>
    {
      const protocol = enforceProtocolWithDosingsTypes(res.data);

      return {
        ...protocol,
        dosings: protocol.dosings.map(d => ({ ...d, deliveryPerListing: Number(d.deliveryPerListing) })) };
    })
    .catch(error => console.log("Error dupliacting protocol " + protocolId + ":", error));
}

export async function getSampleProtocolsToCombineInputXlsx()
{
  return axiosInstance.get('/protocol/sampleProtocolsToCombineInputXlsx', { responseType: 'blob' })
    .then(res =>
    {
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      return blob;
    })
    .catch(error => console.log("Error downloading sample xlsx:", error));
}

export async function postProtocolsToCombineXlsx(xlsx: File, weightings: number[])
{
  const formData = new FormData();
  formData.append('protocolsToCombine', xlsx);
  formData.append('weightings', JSON.stringify(weightings));

  return axiosInstance.post('/protocol/protocolsToCombineXlsx', formData, {
    headers: { 'Content-Type': 'multipart/form-data', weightings },
    responseType: 'blob' })
    .then(res => new Blob([res.data], { type: 'application/zip' }))
    .catch(error => console.log("Error submitting xlsx with protocols to combine:", error));
}

export async function postXlsx(xlsx: File)
{
  const formData = new FormData();
  formData.append('xlsx', xlsx);

  return axiosInstance.post('/protocol/uploadXlsx', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }})
    .then(res => res.data as { sheetNames: string[] })
    .catch(error => console.log("Error submitting xlsx:", error));
}

export async function postProtocolsToCombine(protocols: {
  protocolId?: number,
  mmProtocolId?: number,
  weighting: number }[], userId: number)
{
  return axiosInstance.post('/protocol/protocolsToCombine', { protocols, userId })
    .then(res => res.data.protocolId as number)
    .catch(error => console.log("Error submitting protocols to combine:", error));
}

export async function deleteListing(dosingId: number)
{
  return axiosInstance.post("/protocol/deleteListing", { dosingId })
    .then(resJson => resJson.data)
    .catch(error => console.log("Error deleting vendor from dosing:", error));
}
